import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createRecurringExpenseRequest,
  deleteRecurringExpenseRequest,
  getRecurringExpensesRequest,
  updateRecurringExpenseRequest,
} from "../../api/recurringExpenseApi";
import { CreateRecurringExpenseRequest } from "../../models/RequestModels/CreateRecurringExpenseRequest";
import { UpdateRecurringExpenseRequest } from "../../models/RequestModels/UpdateRecurringExpenseRequest";
import { RecurringExpenseResponse } from "../../models/ResponseModels/RecurringExpenseResponse";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface FetchRecurringExpensesParams {
  property_id?: number;
  unit_id?: number;
  currency?: string;
  active?: boolean;
}

interface UpdateRecurringExpensePayload {
  id: number;
  body: UpdateRecurringExpenseRequest;
}

interface RecurringExpenseState {
  recurringExpenses: RecurringExpenseResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: RecurringExpenseState = {
  recurringExpenses: [],
  loading: false,
  error: null,
};

export const fetchRecurringExpenses = createAsyncThunk(
  "recurringExpenses/fetchRecurringExpenses",
  async (
    params: FetchRecurringExpensesParams | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await getRecurringExpensesRequest(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createRecurringExpense = createAsyncThunk(
  "recurringExpenses/createRecurringExpense",
  async (body: CreateRecurringExpenseRequest, { rejectWithValue }) => {
    try {
      const response = await createRecurringExpenseRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateRecurringExpense = createAsyncThunk(
  "recurringExpenses/updateRecurringExpense",
  async ({ id, body }: UpdateRecurringExpensePayload, { rejectWithValue }) => {
    try {
      const response = await updateRecurringExpenseRequest(id, body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteRecurringExpense = createAsyncThunk(
  "recurringExpenses/deleteRecurringExpense",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteRecurringExpenseRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const recurringExpenseSlice = createSlice({
  name: "recurringExpenses",
  initialState,
  reducers: {
    clearRecurringExpenseError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecurringExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecurringExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.recurringExpenses = action.payload;
      })
      .addCase(fetchRecurringExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to load recurring expenses.",
        );
      })

      .addCase(createRecurringExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRecurringExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.recurringExpenses.unshift(action.payload);
      })
      .addCase(createRecurringExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to create recurring expense.",
        );
      })

      .addCase(updateRecurringExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRecurringExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.recurringExpenses = state.recurringExpenses.map((expense) =>
          expense.id === action.payload.id ? action.payload : expense,
        );
      })
      .addCase(updateRecurringExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to update recurring expense.",
        );
      })

      .addCase(deleteRecurringExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecurringExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.recurringExpenses = state.recurringExpenses.filter(
          (expense) => expense.id !== action.payload,
        );
      })
      .addCase(deleteRecurringExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to delete recurring expense.",
        );
      });
  },
});

export const { clearRecurringExpenseError } = recurringExpenseSlice.actions;

export default recurringExpenseSlice.reducer;
